import { useSiteSettings } from "@/context/SiteSettingsContext";
import { uploadUrl } from "@/services/config";

interface BrandLogoProps {
  /** Sizing classes for the logo image (height + responsive). */
  className?: string;
  /**
   * Surface the logo sits on. "dark" picks the light-on-dark variant (when one
   * is configured) so the mark stays readable on the footer and sign-in panels.
   */
  tone?: "light" | "dark";
  /** Wrap the logo in a light panel (for placement on dark backgrounds). */
  boxed?: boolean;
}

/**
 * Renders the admin-managed logo. On a dark surface it uses the light-on-dark
 * variant when set, falling back to the standard logo otherwise. Falls back to
 * an icon + site name when no logo is configured at all, so the header never
 * renders empty.
 */
export default function BrandLogo({ className = "h-12 md:h-14", tone = "light", boxed = false }: BrandLogoProps) {
  const { settings } = useSiteSettings();

  const resolvedUrl =
    tone === "dark" ? settings.logoDarkUrl || settings.logoUrl : settings.logoUrl;

  if (!resolvedUrl) {
    return (
      <span className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
          <i className="ri-book-2-line text-lg"></i>
        </span>
        <span className={`font-heading text-sm font-extrabold ${tone === "dark" ? "text-background-50" : "text-foreground-950"}`}>
          {settings.siteName}
        </span>
      </span>
    );
  }

  const img = (
    <img
      src={uploadUrl(resolvedUrl)}
      alt={settings.siteName}
      title={settings.siteName}
      className={`${className} w-auto object-contain`}
    />
  );

  if (boxed) {
    return (
      <span className="inline-flex items-center justify-center rounded-lg bg-background-50 px-2.5 py-2">
        {img}
      </span>
    );
  }

  return img;
}